<template>
  <div class="container">
    <h1 class="page-title">MPR - Update Finance Values</h1>
    <div>
      <!-- Global Year and Month Controls -->
      <div>
        <div class="form-cell">
          <label for="global-year">Set Year for All Rows</label><br />
          <select
            v-model="globalYear"
            class="input-field"
            @change="updateAllYears"
          >
            <option v-for="year in years" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
        </div>
        <div class="form-cell">
          <label for="global-month">Set Month for All Rows</label><br />
          <select
            v-model="globalMonth"
            class="input-field"
            @change="updateAllMonths"
          >
            <option value="">Select Month</option>
            <option
              v-for="(month, index) in months"
              :key="index + 1"
              :value="index + 1"
            >
              {{ month }}
            </option>
          </select>
        </div>
      </div>
      <form action="" @submit.prevent="submitForm">
        <!-- Dynamic Rows -->
        <div v-for="(row, index) in rows" :key="index" class="form-row">
          <div class="form-cell">
            <label for="">Year</label><br />
            <select v-model="row.year" class="input-field" required>
              <option v-for="year in years" :key="year">
                {{ year }}
              </option>
            </select>
          </div>

          <div class="form-cell">
            <label for="">Month</label><br />
            <select v-model="row.month" class="input-field" required>
              <option
                v-for="(month, index) in months"
                :key="index + 1"
                :value="index + 1"
              >
                {{ month }}
              </option>
            </select>
          </div>

          <div class="form-cell">
            <label for="">Statement Type</label><br />
            <select v-model="row.factor" class="input-field">
              <option
                v-for="factor in factors"
                :key="factor.id"
                :value="factor.id"
                selected
                required
              >
                {{ factor.mpr_factor }}
              </option>
            </select>
          </div>

          <div class="form-cell">
            <label for="">Value</label><br />
            <input
              type="number"
              step="any"
              v-model="row.value"
              class="input-field"
              placeholder="input value"
              required
            />
          </div>

          <!-- Button to remove a row -->
          <div class="form-cell">
            <button
              type="button"
              @click="removeRow(index)"
              class="remove-row-button"
            >
              Remove
            </button>
          </div>
        </div>

        <!-- Button to add a new row -->
        <button
          type="button"
          @click="addRow"
          class="form-button"
          style="margin-right: 10px"
        >
          Add Row
        </button>

        <!-- Submit Button -->
        <button type="submit" class="form-button">
          Submit <span v-if="isloading" class="loader"></span>
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2";

const months = ref([
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]);

const url = ref();

const years = ref([2024, 2025, 2026, 2027, 2028, 2029, 2030]);
const factors = ref([]); // This will store the factors fetched from the API
const isloading = ref(false);
const globalYear = ref(2025); // Global year control
const globalMonth = ref(""); // Global month control
const rows = ref([
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
  { year: 2025, month: "", factor: "", value: "" },
]); // Initial row setup with empty fields

// Fetch factors from the API
const getFactors = async () => {
  try {
    const { data, status } = await axios.get(
      `${url.value}/api/v1/mpr/get-factors`
    );
    if (status === 200) {
      factors.value = data.data; // Assuming the API returns an array of factor objects
      setDefaultFactors(); // Call function to set default values
    }
  } catch (error) {
    console.error("Error fetching factors:", error);
  }
};

const setDefaultFactors = () => {
  rows.value = rows.value.map((row, index) => ({
    ...row,
    factor: factors.value[index] ? factors.value[index].id : "", // Assign factor sequentially
  }));
};

// Update all rows' year
const updateAllYears = () => {
  rows.value = rows.value.map((row) => ({ ...row, year: globalYear.value }));
};

// Update all rows' month
const updateAllMonths = () => {
  rows.value = rows.value.map((row) => ({ ...row, month: globalMonth.value }));
};

// Add a new row to the form
const addRow = () => {
  const newIndex = rows.value.length;
  rows.value.push({
    year: globalYear.value,
    month: globalMonth.value,
    factor: factors.value[newIndex] ? factors.value[newIndex].id : "",
    value: "",
  });
};

// Remove a specific row
const removeRow = (index) => {
  rows.value.splice(index, 1);
};

// Handle form submission
const submitForm = async () => {
  const payload = rows.value.map((row) => ({
    year: row.year,
    month: row.month,
    factor: row.factor,
    value: row.value,
  }));

  try {
    isloading.value = true;
    const { data, status } = await axios.post(
      `${url.value}/api/v1/mpr/set-values`,
      payload
    );
    if (status === 200 || status == 201) {
      isloading.value = false;
      Swal.fire({
        title: "Success!",
        text: "Data Stored Successfully!",
        icon: "success",
        confirmButtonText: "Ok!",
      });

      console.log("Values successfully submitted", data);
    }
  } catch (error) {
    console.error("Error submitting values:", error);
    isloading.value = false;
  }
};

const settings = ref();
const getUrl = () => {
  // If settings are available in localStorage, load them
  const storedSettings = localStorage.getItem("settings");
  if (storedSettings) {
    settings.value = JSON.parse(storedSettings);
    url.value = settings.value.defaultStates[0].mpr;
    getFactors();
    return;
  }
  getFactors();
};

// Fetch factors when the component is mounted
onMounted(() => {
  getUrl();
});
</script>

<style>
/* General Container Styling */
.container {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
  padding: 10px;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
  flex-wrap: wrap; /* Ensures wrapping on smaller screens */
}

.form-cell {
  flex: 1 1 200px; /* Flex properties to make cells responsive */
  margin-right: 10px;
  margin-bottom: 10px;
}

.form-cell label {
  display: block;
  font-weight: bold;
}

.input-field {
  width: 100%;
  padding: 8px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.remove-row-button {
  background-color: red;
  color: white;
  border: none;
  padding: 5px 10px;
  cursor: pointer;
  margin-top: 10px;
}

.remove-row-button:hover {
  background-color: darkred;
}

.form-button {
  margin-top: 10px;
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  cursor: pointer;
}

.form-button:hover {
  background-color: #0056b3;
}

button {
  margin-top: 10px;
}
</style>
